interface NotificationData {
  url?: string;
  type?: string;
  notificationId?: string;
  metadata?: any;
}

class WebPushService {
  private isSupported: boolean;
  private isInitialized: boolean = false;

  constructor() {
    this.isSupported = this.checkSupport();
  }

  private checkSupport(): boolean {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window &&
      typeof window !== 'undefined'
    );
  }

  async initialize(): Promise<boolean> {
    if (this.isInitialized || !this.isSupported) {
      return false;
    }

    // Skip in development to avoid SW caching interfering with HMR
    if (process.env.NODE_ENV !== 'production') {
      console.warn('Web push service disabled in development');
      return false;
    }

    try {
      // Register service worker
      await this.registerServiceWorker();
      
      // Request notification permission
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        console.warn('Notification permission denied');
        return false;
      }
      
      this.isInitialized = true;
      console.log('Web push service initialized successfully');
      return true;
    } catch (error) {
      console.error('Error initializing web push service:', error);
      return false;
    }
  }

  private async registerServiceWorker(): Promise<void> {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
      
      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      console.log('Service Worker ready');
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      throw error;
    }
  }

  private async requestPermission(): Promise<NotificationPermission> {
    try {
      const permission = await Notification.requestPermission();
      console.log('Notification permission:', permission);
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  async showNotification(title: string, message: string, data?: NotificationData): Promise<void> {
    if (!this.isInitialized) {
      console.warn('Web push service not initialized');
      return;
    }

    try {
      const options: NotificationOptions = {
        body: message,
        icon: '/logo192.png',
        badge: '/logo192.png',
        data: data || { url: '/', type: 'general' },
        requireInteraction: false,
        tag: 'cafelawn-notification'
      };

      const notification = new Notification(title, options);

      // Handle notification click
      notification.onclick = (event) => {
        event.preventDefault();
        notification.close();
        
        const urlToOpen = options.data?.url || '/';
        window.open(urlToOpen, '_blank');
      };

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

    } catch (error) {
      console.error('Error showing notification:', error);
    }
  }

  async sendTestNotification(): Promise<void> {
    await this.showNotification(
      'Test Notification',
      'This is a test notification from CafeLawn',
      { url: '/', type: 'test' }
    );
  }

  async getPermissionStatus(): Promise<NotificationPermission> {
    return Notification.permission;
  }

  async checkBrowserSupport(): Promise<boolean> {
    return this.isSupported;
  }

  // Placeholder for future Firebase integration
  async initializeFirebase(): Promise<void> {
    console.log('Firebase integration not yet implemented');
  }
}

export const webPushService = new WebPushService(); 